import React, { type FunctionComponent } from 'react'
import { css } from '@emotion/react'
import { Text, spacing } from '@retailer-platform/shared-components'
import { MerchandisingFormSection } from 'domains/merchandising/components/form-section/MerchandisingFormSection'
import { type VisibilityConditionDestinationValues } from 'domains/banners/types/visibilityCondition.types'
import { VisibilityConditionTypeEnum } from 'domains/banners/constants/visibilityCondition.constants'
import { CommonDetail } from 'domains/banners/components/visibility-conditions-list/components/visibility-conditions/CommonDetail'
import {
  type ContentManagementVisibilityConditionCountryIds,
  type ContentManagementVisibilityConditionDayOfWeek,
  type ContentManagementVisibilityConditionExpressMembership,
  type ContentManagementVisibilityConditionExpressNonmemberLongTermHoldout,
  type ContentManagementVisibilityConditionMatchesAnyRetailerId,
  type ContentManagementVisibilityConditionMinimumPlatformVersion,
  type ContentManagementVisibilityConditionRetailerAvailability,
  type ContentManagementVisibilityConditionShoppedAtRetailer,
  type ContentManagementVisibilityConditionShopped,
  type ContentManagementVisibilityConditionUserInteractionLimit,
  type ContentManagementVisibilityConditionWhitelistedPostalCodes,
  type ContentManagementVisibilityConditionWhitelistedZones,
} from 'domains/banners/api'

const rowContainer = css({
  display: 'flex',
  marginBottom: spacing.X24,
  width: '100%',
})

export const VisibilityConditionDetail: FunctionComponent<VisibilityConditionDestinationValues> = ({
  condition,
}) => {
  switch (condition.__typename) {
    case VisibilityConditionTypeEnum.COUNTRY_IDS:
      return (
        <CommonDetail
          visibilityConditionId={(condition as ContentManagementVisibilityConditionCountryIds)?.id}
          title="bannersDomain.details.visibility-condition.country-ids.title"
          attributes={[
            {
              name: 'countryIds',
              label: 'bannersDomain.details.visibility-condition.country-ids.country-ids',
              value: (condition as ContentManagementVisibilityConditionCountryIds)?.countryIds,
            },
          ]}
          conditionName="Country Ids"
        />
      )
    case VisibilityConditionTypeEnum.DAY_OF_WEEK:
      return (
        <CommonDetail
          visibilityConditionId={(condition as ContentManagementVisibilityConditionDayOfWeek)?.id}
          title="bannersDomain.details.visibility-condition.day-of-week.title"
          attributes={[
            {
              name: 'daysOfWeek',
              label: 'bannersDomain.details.visibility-condition.day-of-week.days',
              value: (condition as ContentManagementVisibilityConditionDayOfWeek)?.days,
            },
          ]}
          conditionName="Day Of Week"
        />
      )
    case VisibilityConditionTypeEnum.EXPRESS_MEMBERSHIP:
      return (
        <CommonDetail
          visibilityConditionId={
            (condition as ContentManagementVisibilityConditionExpressMembership)?.id
          }
          title="bannersDomain.details.visibility-condition.express-membership.title"
          attributes={[
            {
              name: 'daysOfWeek',
              label:
                'bannersDomain.details.visibility-condition.express-membership.membership-status',
              value: (condition as ContentManagementVisibilityConditionExpressMembership)
                ?.membershipStatus,
            },
          ]}
          conditionName="Express Membership"
        />
      )
    case VisibilityConditionTypeEnum.EXPRESS_NONMEMBER_LONG_TERM_HOLDOUT:
      return (
        <CommonDetail
          visibilityConditionId={
            (condition as ContentManagementVisibilityConditionExpressNonmemberLongTermHoldout)?.id
          }
          title="bannersDomain.details.visibility-condition.express-nonmember-long-term-holdout.title"
          attributes={[]}
          conditionName="Express Nonmember Long Term Holdout"
        />
      )
    case VisibilityConditionTypeEnum.MATCHES_ANY_RETAILER_ID:
      return (
        <CommonDetail
          visibilityConditionId={
            (condition as ContentManagementVisibilityConditionMatchesAnyRetailerId)?.id
          }
          title="bannersDomain.details.visibility-condition.matches-any-retailer-id.title"
          attributes={[
            {
              name: 'retailerIds',
              label:
                'bannersDomain.details.visibility-condition.matches-any-retailer-id.retailer-ids',
              value: (condition as ContentManagementVisibilityConditionMatchesAnyRetailerId)
                ?.retailerIds,
            },
          ]}
          conditionName="Matches Any Retailer Id"
        />
      )
    case VisibilityConditionTypeEnum.MINIMUM_PLATFORM_VERSION:
      return (
        <CommonDetail
          visibilityConditionId={
            (condition as ContentManagementVisibilityConditionMinimumPlatformVersion)?.id
          }
          title="bannersDomain.details.visibility-condition.minimum-platform-version.title"
          attributes={[
            {
              name: 'platform',
              label: 'bannersDomain.details.visibility-condition.minimum-platform-version.platform',
              value: (condition as ContentManagementVisibilityConditionMinimumPlatformVersion)
                ?.platform,
            },
            {
              name: 'version',
              label: 'bannersDomain.details.visibility-condition.minimum-platform-version.version',
              value: (condition as ContentManagementVisibilityConditionMinimumPlatformVersion)
                ?.version,
            },
          ]}
          conditionName="Minimum Platform Version"
        />
      )
    case VisibilityConditionTypeEnum.RETAILER_AVAILABILITY:
      return (
        <CommonDetail
          visibilityConditionId={
            (condition as ContentManagementVisibilityConditionRetailerAvailability)?.id
          }
          title="bannersDomain.details.visibility-condition.retailer-availability.title"
          attributes={[
            {
              name: 'retailerId',
              label: 'bannersDomain.details.visibility-condition.retailer-availability.retailer-id',
              value: (condition as ContentManagementVisibilityConditionRetailerAvailability)
                ?.retailerId,
            },
          ]}
          conditionName="Retailer Availability"
        />
      )
    case VisibilityConditionTypeEnum.SHOPPED_AT_RETAILER:
      return (
        <CommonDetail
          visibilityConditionId={
            (condition as ContentManagementVisibilityConditionShoppedAtRetailer)?.id
          }
          title="bannersDomain.details.visibility-condition.shopped-at-retailer.title"
          attributes={[
            {
              name: 'retailerId',
              label: 'bannersDomain.details.visibility-condition.shopped-at-retailer.retailer-id',
              value: (condition as ContentManagementVisibilityConditionShoppedAtRetailer)
                ?.retailerId,
            },
            {
              name: 'hasShopped',
              label: 'bannersDomain.details.visibility-condition.shopped-at-retailer.has-shopped',
              value: (condition as ContentManagementVisibilityConditionShoppedAtRetailer)
                ?.hasShopped,
            },
            {
              name: 'numberOfDays',
              label:
                'bannersDomain.details.visibility-condition.shopped-at-retailer.number-of-days',
              value: (condition as ContentManagementVisibilityConditionShoppedAtRetailer)
                ?.numberOfDays,
            },
            {
              name: 'countryId',
              label: 'bannersDomain.details.visibility-condition.shopped-at-retailer.country-id',
              value: (condition as ContentManagementVisibilityConditionShoppedAtRetailer)
                ?.countryId,
            },
            {
              name: 'storeConfigurationId',
              label:
                'bannersDomain.details.visibility-condition.shopped-at-retailer.store-configuration-id',
              value: (condition as ContentManagementVisibilityConditionShoppedAtRetailer)
                ?.storeConfigurationId,
            },
          ]}
          conditionName="Shopped At Retailer"
        />
      )
    case VisibilityConditionTypeEnum.SHOPPED:
      return (
        <CommonDetail
          visibilityConditionId={(condition as ContentManagementVisibilityConditionShopped)?.id}
          title="bannersDomain.details.visibility-condition.shopped.title"
          attributes={[
            {
              name: 'hasShopped',
              label: 'bannersDomain.details.visibility-condition.shopped.has-shopped',
              value: (condition as ContentManagementVisibilityConditionShopped)?.hasShopped,
            },
            {
              name: 'countryId',
              label: 'bannersDomain.details.visibility-condition.shopped.country-id',
              value: (condition as ContentManagementVisibilityConditionShopped)?.countryId,
            },
            {
              name: 'storeConfigurationId',
              label: 'bannersDomain.details.visibility-condition.shopped.store-configuration-id',
              value: (condition as ContentManagementVisibilityConditionShopped)
                ?.storeConfigurationId,
            },
          ]}
          conditionName="Shopped"
        />
      )
    case VisibilityConditionTypeEnum.USER_INTERACTION_LIMIT:
      return (
        <CommonDetail
          visibilityConditionId={
            (condition as ContentManagementVisibilityConditionUserInteractionLimit)?.id
          }
          title="bannersDomain.details.visibility-condition.user-interaction-limit.title"
          attributes={[
            {
              name: 'maxImpressionsCount',
              label:
                'bannersDomain.details.visibility-condition.user-interaction-limit.max-impressions-count',
              value: (condition as ContentManagementVisibilityConditionUserInteractionLimit)
                ?.maxImpressionsCount,
            },
            {
              name: 'maxEngagementsCount',
              label:
                'bannersDomain.details.visibility-condition.user-interaction-limit.max-engagements-count',
              value: (condition as ContentManagementVisibilityConditionUserInteractionLimit)
                ?.maxEngagementsCount,
            },
            {
              name: 'maxDismissedCount',
              label:
                'bannersDomain.details.visibility-condition.user-interaction-limit.max-dismissed-count',
              value: (condition as ContentManagementVisibilityConditionUserInteractionLimit)
                ?.maxDismissedCount,
            },
            {
              name: 'countCooldownSec',
              label:
                'bannersDomain.details.visibility-condition.user-interaction-limit.count-cooldown-sec',
              value: (condition as ContentManagementVisibilityConditionUserInteractionLimit)
                ?.countCooldownSec,
            },
          ]}
          conditionName="User Interaction Limit"
        />
      )
    case VisibilityConditionTypeEnum.WHITELISTED_POSTAL_CODES:
      return (
        <CommonDetail
          visibilityConditionId={
            (condition as ContentManagementVisibilityConditionWhitelistedPostalCodes)?.id
          }
          title="bannersDomain.details.visibility-condition.whitelisted-postal-codes.title"
          attributes={[
            {
              name: 'whitelistedPostalCodes',
              label:
                'bannersDomain.details.visibility-condition.whitelisted-postal-codes.postal-codes',
              value: (condition as ContentManagementVisibilityConditionWhitelistedPostalCodes)
                ?.postalCodes,
            },
          ]}
          conditionName="Whitelisted Postal Codes"
        />
      )
    case VisibilityConditionTypeEnum.WHITELISTED_ZONES:
      return (
        <CommonDetail
          visibilityConditionId={
            (condition as ContentManagementVisibilityConditionWhitelistedZones)?.id
          }
          title="bannersDomain.details.visibility-condition.whitelisted-zones.title"
          attributes={[
            {
              name: 'whitelistedZones',
              label: 'bannersDomain.details.visibility-condition.whitelisted-zones.zones',
              value: (condition as ContentManagementVisibilityConditionWhitelistedZones)?.zones,
            },
          ]}
          conditionName="Whitelisted Zones"
        />
      )
    default:
      return (
        <MerchandisingFormSection titleId="bannersDomain.details.visibility-condition.unsupported.title">
          <div css={rowContainer}>
            {Object.entries(condition).map(([key, value]) => (
              <div key={`${key}-${value}`}>
                <Text weight="semibold">{key}</Text>
                <Text size="medium">{value}</Text>
              </div>
            ))}
          </div>
        </MerchandisingFormSection>
      )
  }
}
